import { Box, Checkbox, FormControlLabel, useMediaQuery } from "@mui/material";
import { Formik, FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import { SuccessUploadIcon } from "../../assets/iconComponents/SuccessUploadIcon";
import { missingRequisitions } from "../../recoil/atoms";
import { DetailedRequisitionSerializer } from "../../recoil/types";
import { useRequisitionSetter } from "../../recoil/useRequisitionSetter";
import {
  RefreshMissingInfo,
  RefreshRequisitionRows,
} from "../Grid/Services/GridService";
import { Button } from "../shared/Button/Button";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import FormField from "../shared/TextField/FormField";
import FileUploader, { FileUploadInfo } from "./FileUploader";
import {
  GetInsuranceList,
  PatchSpecificRequisition,
  UpdateSpecificRequsitionRequestType,
  updateSpecificRequsitionInitialValues,
} from "./Services/UserReportService";

import {
  BillingFileUploaderWrapper,
  EditReport,
  ErrorText,
  ErrorWrapper,
  BillingFieldRow,
  GeneralInfoText,
  BillingInfoCard,
  UserReportGenerateNotesConfirmationGrid,
  UserReportGenerateNotesConfirmationsWrapper,
  UserReportGenerateNotesConfirmationText,
} from "./UserReport.styles";
import { formatWithTimezoneOffset } from "../../utils/helpers";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

const BillingFormField = (props: React.ComponentProps<typeof FormField>) => (
  <FormField {...props} cardDisplay />
);

interface InsuranceCarrier {
  Label: string;
  Value: string;
}

// SWE-4918: canonical string the codebase already recognizes for a pending-Medicaid
// patient (matches backend's PENDING_MEDICAID constant). Keep these in sync.
const PENDING_MEDICAID_LABEL = "Pending Medicaid";
const PENDING_MEDICAID_ID_PLACEHOLDER = "0";

const BillingInfo: React.FC<{
  fullReport: DetailedRequisitionSerializer;
  setFullReport: React.Dispatch<
    React.SetStateAction<DetailedRequisitionSerializer | undefined>
  >;
}> = ({ fullReport, setFullReport }) => {
  const [editMode, setEditMode] = useState(false);
  const [, setMissingReqs] = useRecoilState(missingRequisitions);
  const { auth, setLoggedOut } = useAuthenticationHook();
  const { setRequisitions, searchText } = useRequisitionSetter();
  const [loading, setLoading] = useState(false);
  const [insuranceList, setInsuranceList] = useState<InsuranceCarrier[]>([]);
  const [processing, setProcessing] = useState(false);
  const [fileUploads, setFileUploads] = useState<FileUploadInfo>({});

  // SWE-4918: The Pending Medicaid checkbox sits in its own row in the left
  // (insurance) column, above Primary Insurance. Because the left and right
  // fields share flex rows, that checkbox row would otherwise push the entire
  // right (ICD/address) column down by its height, leaving an uneven gap below
  // ICD #1. On desktop we pull the right-column fields below the checkbox back
  // up by the checkbox row height so the right column stays evenly spaced. On
  // mobile every field stacks in a single column, so no compensation is needed.
  const isDesktopLayout = useMediaQuery("(min-width:769px)");
  const showPendingMedicaidCheckbox =
    fullReport.is_missing_billing_info &&
    !fullReport.primary_insurance &&
    !fullReport.primary_insurance_id &&
    !fullReport.primary_insurance_group;
  const PENDING_MEDICAID_ROW_HEIGHT_PX = 44;
  const rightColumnShift =
    showPendingMedicaidCheckbox && isDesktopLayout
      ? { marginTop: `-${PENDING_MEDICAID_ROW_HEIGHT_PX}px` }
      : {};

  useEffect(() => {
    const getMissingInsuranceList = async () => {
      if (fullReport.is_missing_billing_info && auth) {
        try {
          const insuranceListResponse = await GetInsuranceList(
            auth!.token,
            setLoggedOut,
          );
          const newInsuranceList = await insuranceListResponse.json();
          setInsuranceList(newInsuranceList.records);
        } catch (e) {
          //
        }
      }
    };
    getMissingInsuranceList();
  }, [auth, fullReport.is_missing_billing_info]);

  return (
    <div>
      {!loading ? (
        <Formik<UpdateSpecificRequsitionRequestType>
          initialValues={{
            ...updateSpecificRequsitionInitialValues,
          }}
          onSubmit={async (values, { resetForm }) => {
            setLoading(true);
            try {
              const response = await PatchSpecificRequisition(
                auth!.token,
                fullReport.sfid!,
                {
                  ...updateSpecificRequsitionInitialValues,
                  ...values,
                },
                setLoggedOut,
              );
              setLoading(false);
              if (response?.ok) {
                resetForm();
                const newReport = await response.json();
                setFullReport(newReport);
                await RefreshRequisitionRows(
                  auth!.token,
                  setRequisitions,
                  setLoggedOut,
                  searchText,
                );
                await RefreshMissingInfo(
                  auth!.token,
                  setMissingReqs,
                  setLoggedOut,
                );
              } else {
                // setResponseMessage("There was an error - please try again.");
              }
            } catch (e) {
              setLoading(false);
              // setResponseMessage(networkErrorMessage);
            }
          }}
          validateOnChange={false}
        >
          {({
            values,
            handleSubmit,
            setFieldValue,
            resetForm,
          }: FormikProps<UpdateSpecificRequsitionRequestType>) => (
            <BillingInfoCard>
              <GeneralInfoText id="req-billing-information">
                BILLING INFORMATION
              </GeneralInfoText>
              {fullReport.is_missing_billing_info && (
                <ErrorWrapper>
                  <ErrorText>
                    <ExclamationIcon />
                    <div style={{ paddingTop: "3px" }}>
                      Some details need your attention. Please provide the
                      required information to complete the order.
                    </div>
                  </ErrorText>
                </ErrorWrapper>
              )}
              {fullReport.has_uploaded_documents &&
                fullReport.is_missing_billing_info && (
                  <UserReportGenerateNotesConfirmationsWrapper id="req-treatment-notes">
                    <UserReportGenerateNotesConfirmationGrid>
                      <SuccessUploadIcon />
                      <UserReportGenerateNotesConfirmationText>
                        Files have been uploaded and are being reviewed. We will
                        update status within 24 hours.
                      </UserReportGenerateNotesConfirmationText>
                    </UserReportGenerateNotesConfirmationGrid>
                  </UserReportGenerateNotesConfirmationsWrapper>
                )}

              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Employer"
                  fieldValue={fullReport.employer || ""}
                />
                <BillingFormField
                  fieldTitle="ICD #1"
                  fieldValue={fullReport.icd_1 || values.icd_1}
                  onFieldChange={(val) => setFieldValue("icd_1", val)}
                  isError={
                    fullReport.is_missing_billing_info && !fullReport.icd_1
                  }
                  edit={
                    fullReport.is_missing_billing_info &&
                    !fullReport.icd_1 &&
                    editMode
                  }
                />
              </BillingFieldRow>
              {showPendingMedicaidCheckbox && (
                <BillingFieldRow>
                  <FormControlLabel
                    sx={{ width: "50%", margin: "5px 0" }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ padding: "0 6px 0 0" }}
                        disabled={!editMode}
                        checked={Boolean(values.is_pending_medicaid)}
                        onChange={(e) => {
                          const { checked } = e.target;
                          setFieldValue("is_pending_medicaid", checked);
                          if (checked) {
                            setFieldValue(
                              "primary_insurance",
                              PENDING_MEDICAID_LABEL,
                            );
                            setFieldValue(
                              "primary_insurance_id",
                              PENDING_MEDICAID_ID_PLACEHOLDER,
                            );
                            setFieldValue(
                              "primary_insurance_group",
                              PENDING_MEDICAID_ID_PLACEHOLDER,
                            );
                          } else {
                            setFieldValue("primary_insurance", "");
                            setFieldValue("primary_insurance_id", "");
                            setFieldValue("primary_insurance_group", "");
                          }
                        }}
                      />
                    }
                    label={
                      <Box sx={{ fontSize: "17px" }}>
                        Pending Medicaid (awaiting card)
                      </Box>
                    }
                  />
                  <div style={{ width: "50%" }} />
                </BillingFieldRow>
              )}
              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Primary Insurance"
                  fieldType="autocomplete"
                  autoCompleteOptions={insuranceList.map(
                    (insurance) => insurance.Label,
                  )}
                  fieldValue={
                    fullReport.primary_insurance || values.primary_insurance
                  }
                  onFieldChange={(val) =>
                    setFieldValue("primary_insurance", val)
                  }
                  placeholderOnError="Add Primary insurance"
                  isError={
                    fullReport.is_missing_billing_info &&
                    !fullReport.primary_insurance &&
                    !values.is_pending_medicaid
                  }
                  edit={
                    fullReport.is_missing_billing_info &&
                    !fullReport.primary_insurance &&
                    editMode &&
                    !values.is_pending_medicaid
                  }
                />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="ICD #2"
                  fieldValue={fullReport.icd_2 || ""}
                />
              </BillingFieldRow>
              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Primary Insurance Group #"
                  fieldValue={
                    fullReport.primary_insurance_group ||
                    values.primary_insurance_group
                  }
                  onFieldChange={(val) =>
                    setFieldValue("primary_insurance_group", val)
                  }
                  placeholderOnError="Add Primary Insurance Group #"
                  isError={
                    fullReport.is_missing_billing_info &&
                    !fullReport.primary_insurance_group &&
                    !values.is_pending_medicaid
                  }
                  edit={
                    (fullReport.is_missing_billing_info ||
                      !fullReport.primary_insurance_group) &&
                    editMode &&
                    !values.is_pending_medicaid
                  }
                />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="ICD #3"
                  fieldValue={fullReport.icd_3 || ""}
                />
              </BillingFieldRow>
              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Primary Insurance ID"
                  fieldValue={
                    fullReport.primary_insurance_id ||
                    values.primary_insurance_id
                  }
                  onFieldChange={(val) =>
                    setFieldValue("primary_insurance_id", val)
                  }
                  placeholderOnError="Add Primary Insurance ID"
                  isError={
                    fullReport.is_missing_billing_info &&
                    !fullReport.primary_insurance_id &&
                    !values.is_pending_medicaid
                  }
                  edit={
                    (fullReport.is_missing_billing_info ||
                      !fullReport.primary_insurance_id) &&
                    editMode &&
                    !values.is_pending_medicaid
                  }
                />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="Remaining ICD Codes"
                  fieldValue={fullReport.remaining_icd_codes || ""}
                  placeholderOnError="Remaining ICD Codes"
                />
              </BillingFieldRow>
              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Subscriber Name"
                  fieldValue={fullReport.subscriber_name || ""}
                  placeholderOnError="Subscriber Name"
                />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="Street Address"
                  fieldValue={
                    fullReport.street_address_1 || values.street_address_1
                  }
                  onFieldChange={(val) =>
                    setFieldValue("street_address_1", val)
                  }
                  placeholderOnError="Street Address"
                  isError={
                    fullReport.is_missing_billing_info &&
                    !fullReport.street_address_1
                  }
                  edit={
                    fullReport.is_missing_billing_info &&
                    !fullReport.street_address_1 &&
                    editMode
                  }
                />
              </BillingFieldRow>
              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Subscriber Date of Birth"
                  fieldValue={
                    formatWithTimezoneOffset(
                      fullReport.subscriber_date_of_birth,
                    ) ||
                    fullReport.subscriber_date_of_birth ||
                    ""
                  }
                  fieldType="date"
                />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="City"
                  fieldValue={fullReport.city || values.city}
                  onFieldChange={(val) => setFieldValue("city", val)}
                  placeholderOnError="City"
                  isError={
                    fullReport.is_missing_billing_info && !fullReport.city
                  }
                  edit={
                    fullReport.is_missing_billing_info &&
                    !fullReport.city &&
                    editMode
                  }
                />
              </BillingFieldRow>
              <BillingFieldRow>
                <BillingFormField
                  fieldTitle="Relation to Insured"
                  fieldValue={fullReport.relationship_to_insured || ""}
                />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="State"
                  fieldValue={fullReport.state || values.state}
                  onFieldChange={(val) => setFieldValue("state", val)}
                  placeholderOnError="State"
                  isError={
                    fullReport.is_missing_billing_info && !fullReport.state
                  }
                  edit={
                    fullReport.is_missing_billing_info &&
                    !fullReport.state &&
                    editMode
                  }
                />
              </BillingFieldRow>
              <BillingFieldRow>
                <div style={{ width: "50%" }} />
                <BillingFormField
                  styles={rightColumnShift}
                  fieldTitle="Zip"
                  fieldValue={fullReport.zip || values.zip}
                  onFieldChange={(val) => setFieldValue("zip", val)}
                  placeholderOnError="Zip"
                  isError={
                    fullReport.is_missing_billing_info && !fullReport.zip
                  }
                  edit={
                    fullReport.is_missing_billing_info &&
                    !fullReport.zip &&
                    editMode
                  }
                />
              </BillingFieldRow>
              {fullReport.is_missing_billing_info && (
                <EditReport>
                  {!editMode && (
                    <Button
                      label="EDIT"
                      onClick={() => setEditMode(true)}
                      size="medium"
                    />
                  )}
                  {editMode && (
                    <Box sx={{ display: "flex", gap: "15px" }}>
                      <Button
                        label="CANCEL"
                        onClick={() => {
                          resetForm();
                          setEditMode(false);
                        }}
                        size="medium"
                      />
                      <Button
                        label="SAVE"
                        onClick={() => {
                          handleSubmit();
                          setEditMode(false);
                        }}
                        size="medium"
                        reverse
                        disabled={
                          JSON.stringify(
                            updateSpecificRequsitionInitialValues,
                          ) === JSON.stringify(values)
                        }
                      />
                    </Box>
                  )}
                </EditReport>
              )}
              <BillingFileUploaderWrapper id="billing-file-uploader">
                <FileUploader
                  fullReport={fullReport}
                  setUploadProcessing={setProcessing}
                  uploadProcessing={processing}
                  value={fileUploads}
                  setValue={setFileUploads}
                  setFullReport={setFullReport}
                />
              </BillingFileUploaderWrapper>
            </BillingInfoCard>
          )}
        </Formik>
      ) : (
        <div
          style={{
            height: "100%",
            width: "100%",
            paddingBottom: "100px",
          }}
        >
          <SpinnerLoading />
        </div>
      )}
    </div>
  );
};

export default BillingInfo;
